import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ShieldAlert, Upload, Loader2, Lock } from "lucide-react";
import authService from "../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Aviso/bloqueio de certificado digital obrigatório - regra de negócio
 * confirmada: fornecedor e afiliado (Brasil e Internacional) têm 7 dias
 * a partir do cadastro para enviar o certificado A1 deles, ou a conta
 * fica bloqueada. Isso é diferente do certificado do admin (que serve
 * pra login) - aqui é uma exigência de segurança/conformidade da conta.
 */
const AvisoCertificadoObrigatorio = ({ usuarioId, children }) => {
  const [status, setStatus] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);

  const carregarStatus = async () => {
    try {
      const resp = await authService.fetchAuth(`${BACKEND_URL}/api/certificado-usuario/status/${usuarioId}`);
      if (resp.ok) setStatus(await resp.json());
    } catch (error) {
      console.error("Erro ao verificar certificado:", error);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    if (usuarioId) carregarStatus();
    // eslint-disable-next-line
  }, [usuarioId]);

  const enviarCertificado = async () => {
    if (!arquivo) {
      toast.error("Selecione o arquivo do certificado (.pfx ou .p12).");
      return;
    }
    setEnviando(true);
    try {
      const dadosForm = new FormData();
      dadosForm.append("file", arquivo);
      const resp = await authService.fetchAuth(
        `${BACKEND_URL}/api/certificado-usuario/upload?usuario_id=${usuarioId}`,
        { method: "POST", body: dadosForm }
      );
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao enviar certificado");
      toast.success("Certificado enviado! Sua conta está liberada.");
      await carregarStatus();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setEnviando(false);
    }
  };

  if (carregando || !status || status.tem_certificado) {
    return children;
  }

  // Bloqueado - passou dos 7 dias sem enviar
  if (status.bloqueado) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-start sm:items-center justify-center p-4 py-8 overflow-y-auto">
        <Card className="p-8 max-w-md w-full text-center bg-gray-900 border-red-800">
          <Lock className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Conta Bloqueada</h2>
          <p className="text-gray-400 mb-6">
            O prazo de 7 dias para enviar seu certificado digital A1 já passou. Envie agora para liberar sua conta.
          </p>
          <input
            type="file"
            accept=".pfx,.p12"
            onChange={(e) => setArquivo(e.target.files?.[0] || null)}
            className="w-full text-sm text-gray-400 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gray-800 file:text-gray-300 mb-4"
          />
          <Button onClick={enviarCertificado} disabled={enviando} className="w-full bg-orange-600 hover:bg-orange-500">
            {enviando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
            Enviar Certificado
          </Button>
        </Card>
      </div>
    );
  }

  // Ainda dentro do prazo - mostra aviso, mas deixa usar o sistema normal
  return (
    <div className="flex flex-col h-full">
      <div className="bg-yellow-900/30 border-b border-yellow-800 px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-yellow-400 text-sm">
          <ShieldAlert className="w-4 h-4 shrink-0" />
          <span>
            Envie seu certificado digital A1 em até <strong>{status.dias_restantes} dia(s)</strong>, ou sua conta será bloqueada.
          </span>
        </div>
        <label className="shrink-0">
          <input type="file" accept=".pfx,.p12" className="hidden" onChange={(e) => { setArquivo(e.target.files?.[0] || null); }} />
          <span className="text-xs bg-yellow-700 hover:bg-yellow-600 text-white px-3 py-1.5 rounded-md cursor-pointer">
            Enviar agora
          </span>
        </label>
      </div>
      {arquivo && (
        <div className="bg-gray-800 px-4 py-2 flex items-center justify-between">
          <span className="text-xs text-gray-300">{arquivo.name}</span>
          <Button size="sm" onClick={enviarCertificado} disabled={enviando} className="bg-orange-600 hover:bg-orange-500 h-7 text-xs">
            {enviando ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirmar envio"}
          </Button>
        </div>
      )}
      {children}
    </div>
  );
};

export default AvisoCertificadoObrigatorio;
